<template>
  <div>
    <vx-card class="mb-5" title="Basic Principles of Teaching">
      <p class="mb-5">
        As students, we can tend to take for granted that we learn what the teacher presents to us -
        without necessarily thinking about the techniques that the teachers are actually using to
        get us there.
      </p>
      <p class="mb-5">
        When you’re coming up with a lesson plan or a way of showing someone how to do something,
        you’ll most likely want to work through these stages with a student:
      </p>
      <h5 class="mb-5">Modelling</h5>

      <p class="mb-5">
        Learning new skills or ideas would take a very long time and a lot of hardship if we were
        simply left to our own devices. Can you imagine cooking an egg or tying your shoes without
        having ever seen someone do it before?
      </p>
      <p class="mb-5">
        At its most basic, modelling is taking students through a new task by doing it yourself and
        allowing your students to watch. For example, a Maths teacher might take a question from the
        textbook and work their way through it on the board so that students can understand the
        method.
      </p>
      <p class="mb-5">
        One of the ways that you can elevate this practice is to do ‘metacognitive modelling’.
        Metacognitive means ‘thinking about how you think’, so metacognitive modelling means to talk
        through your thinking process as you go through the task.
      </p>
      <p class="mb-5">
        It’s also worth remembering that you can apply the concept of modelling to more than just
        the curriculum content. Much like your parents or role models, if you don’t want certain
        behaviours in the classroom, it’s important that you don’t engage in those behaviours
        yourself. For example, if you want students to treat each other with respect, it’s crucial
        that students see you treating students and peers with respect.
      </p>

      <h5 class="mb-5">Supported working</h5>
      <p class="mb-5">
        Once you have modelled the skill to your students, you might take the opportunity to allow
        them to try and tackle the task themselves, with your guidance. It’s like teaching a kid to
        ride a bicycle. You might start out by giving them a lot of balance assistance and support,
        but as they gain confidence, you’re able to provide them with less support over time until
        they develop the capacity to work on their skills independently.
      </p>
      <p class="mb-5">
        The value of supported working is that students are given the opportunity to self-assess as
        to whether they need help or not as they work their way through a task. It allows students
        to keep building their metacognition skills and identify which parts of a task are causing
        them to get ‘stuck’. You might encourage this type of learning by asking open-ended
        questions to drive students’ reflection, prediction or imagination.
      </p>
      <h5 class="mb-5">Independent work</h5>
      <p class="mb-5">
        Independent work is a great indicator that your students have acquired the skills you’ve set
        out to teach them. It doesn’t mean that you aren’t checking in on your students at all. It
        just means that they have the opportunity to work through some problems themselves or with
        peers before relying on your expertise.
      </p>
      <p class="mb-5">
        Typically in a classroom setting, you’ll start out with small time increments and easier
        tasks, and work your way up to more advanced concepts and tasks as you build their skills
        and self-reliance.
      </p>
    </vx-card>

    <vx-card>
      <h4 class="mb-5">Bloom’s Taxonomy</h4>
      <centered-image-wrapper
        :image="image1"
        classes="2/3"
        alt="Bloom's Taxonomy pyramid with six levels from bottom to top. Remember: recall facts and basic concepts (define, duplicate, list, memorize, repeat, state). Understand: explain ideas or concepts (classify, describe, discuss, explain, identify, locate, recognize, report, select, translate). Apply: use information in new situations (execute, implement, solve, use, demonstrate, interpret, operate, schedule, sketch). Analyze: draw connections among ideas (differentiate, organize, relate, compare, contrast, distinguish, examine, experiment, question, test). Evaluate: justify a stand or decision (appraise, argue, defend, judge, select, support, value, critique, weigh). Create: produce new or original work (design, assemble, construct, conjecture, develop, formulate, author, investigate). Source: Vanderbilt University Center for Teaching."
      />
      <p class="mb-5">
        Bloom’s Taxonomy is a framework designed by Benjamin Bloom (and others) to categorise
        educational goals and skills. In this diagram, the ‘lower-order’ thinking skills are at the
        bottom, with the more advanced skills appearing near the top of the ‘pyramid’.
      </p>
      <p class="mb-5">
        This basically means that tasks that relate to the higher levels of the taxonomy require a
        deeper understanding of the subject matter. It’s worth noting that there can be crossover
        between the levels, so it’s not so much as a cut-and-dry separation of activities as it is a
        method to approximate difficulty and engagement levels between tasks.
      </p>
      <p class="mb-5">
        Let’s take a closer look at what the levels of the Taxonomy are and how they can relate to
        tasks in the classroom.
      </p>
      <h5 class="mb-5">Remember</h5>
      <p class="mb-5">
        This area is the foundation of all learning, that all other levels build upon. Its purpose
        is to make sure that students have the facts they need at their disposal before moving on to
        higher levels. This area would involve students being able to recite or state facts and
        definitions that you’ve taught them.
      </p>
      <h5 class="mb-5">Understand</h5>
      <p class="mb-5">
        Understanding goes one level beyond and is about assessing if students know the facts well
        enough to be able to reproduce their meaning in their own words. Where reciting is asking
        students to provide a fact or definition you have given them, to show understanding students
        need to be able to provide an explanation in their own words. This could include making
        their own definition, providing an example, or writing a summary of a longer text they have
        been given.
      </p>
      <h5 class="mb-5">Apply</h5>
      <p class="mb-5">
        Applying involves using those facts, concepts or procedures to complete a task. This
        challenges students to think about the relevance of the information they’ve learned to other
        areas of the subject.
      </p>
      <p class="mb-5">
        Now that students have internalised the meaning and definition of the idea or procedure, the
        next step is to put it into practice. An example of a task using the <b>Apply</b> framework
        could be to first <b>Remember</b> and <b>Understand</b> the process of long division, then
        be asked to <b>apply</b> the method of long division to a new equation students haven’t seen
        before.
      </p>
      <h5 class="mb-5">Analyse</h5>
      <p class="mb-5">
        Analysing involves breaking down information into its relevant parts using their own
        judgement. Once students understand the underlying theory and structure behind the
        information, it allows them to compare and contrast one text or fact to another. Typically
        at this level students will be asked to <b>compare and contrast</b>, create
        <b>diagrams</b> or <b>distinguish</b> fact from fiction.
      </p>
      <h5 class="mb-5">Evaluate</h5>
      <p class="mb-5">
        Evaluating builds on a lot of the skills developed in the <b>analysis</b> area of the
        Taxonomy, but then asks students to create their original appraisal of the material they’ve
        learned, using the knowledge at their disposal. At this level, students are asked to find
        effective solutions to problems, justify decisions or identify errors in thinking in others’
        or even their own work.
      </p>
      <h5 class="mb-5">Create</h5>
      <p class="mb-5">
        This is considered to be the ‘highest-order’ skills level - meaning the one which requires
        the most knowledge and the application of relevant skills at students’ disposal.
      </p>
      <p class="mb-5">
        This may seem confusing, but if you’ve ever sat down to write a short story, or draw a
        picture, you’ll quickly realise that in order to create something original, you need to have
        a deep understanding of what structures, methods and knowledge areas are required and how to
        use them.
      </p>
      <p class="mb-5">
        For instance, great writers understand plot structure, characterisation, and descriptive
        language, and will often combine these with their lived experiences or original research to
        write a compelling and engaging story.
      </p>
      <p class="mb-5">
        You probably won’t ask your students to write the next Great Australian Novel - but asking
        them to create a brochure, write a short story or script or build a model all come under the
        <b>create</b> umbrella.
      </p>
    </vx-card>
    <vx-card>
      <h4 class="mb-5">Classroom management</h4>

      <p class="mb-5">
        Teaching one-on-one is one thing, but teaching in a group context is another thing entirely.
        As a teacher, it’s not just about being able to deliver the content. It’s also about being
        able to create an atmosphere that’s conducive to learning.
      </p>
      <p class="mb-5">Some important aspects when it comes to classroom management include:</p>

      <h5 class="mb-5">Establishing behavioural expectations and boundaries</h5>

      <p class="mb-5">
        A classroom is like its own little community, and as the community leader, it is up to you
        to set how you want your classroom to be run. Younger people thrive on a sense of structure,
        so it’s important to set the rules for your classroom and apply them consistently from
        lesson to lesson.
      </p>
      <p class="mb-5">
        Behavioural expectations might include things like what language is allowed in the
        classroom, the level of noise that you’re willing to tolerate while they complete tasks, or
        what equipment they need to get out when they arrive in your classroom.
      </p>
      <p class="mb-5">
        These principles are also very useful for students as they prepare to enter their working
        lives as adults, where being able to understand and respond to workplace procedures and
        internal cultures becomes a vital part of their success.
      </p>

      <h5 class="mb-5">Developing effective working relationships with your students</h5>

      <p class="mb-5">
        There are two important aspects to this practice. The first is that in order to be able to
        teach a classroom full of students at the same time, you need to establish a level of trust
        and communication with each of your students.
      </p>
      <p class="mb-5">
        Students need safety, inclusion, responsibility, fun and freedom to create an optimal
        learning environment, so effective teachers try and build their lesson plans and classrooms
        to meet these expectations and principles.
      </p>

      <h5 class="mb-5">Being invested in student welfare</h5>

      <p class="mb-5">
        It’s important to acknowledge that a student might be exhibiting difficult or oppositional
        behaviours because their needs aren’t being met in some way. While it doesn’t necessarily
        excuse the behaviour, it can help to reveal the underlying needs that are generating this
        behaviour, which means you can create solutions.
      </p>
      <p class="mb-5">
        For example, a student might be disruptive because they feel inadequately equipped to take
        on the task at hand. This could potentially be resolved by adapting the lesson plan, or
        investing some extra time to find out where the student is getting stuck and finding a
        different approach to the problem.
      </p>
      <p class="mb-5">
        As a teacher, you also have what is called a duty of care for your students. This basically
        means that part of your job is to identify any potential issues students may be having with
        their wellbeing, and if needed to discuss these issues with year advisors, careers advisors,
        parents, or principals.
      </p>
    </vx-card>
  </div>
</template>

<script>
import store from '@/store/store';

export default {
  data() {
    return {
      image1: require('@/assets/images/experiences/teacher/bloom_taxonomy.jpg'),
    };
  },
  beforeMount() {
    store.commit('authStudent/setActiveExperienceID', this.$route.params.id);
  },
};
</script>
